/**
 * Fetch wrapper for the engine's internal API, called directly on the engine's host.
 *
 * The engine host is a destination of this console's Cloudflare Access application, so the
 * operator's Access cookie rides along and Access stamps the assertion the engine verifies.
 *
 * The engine serves real transport statuses on `/internal/v1/**` (it deliberately skips the
 * device's always-200 envelope), so a non-2xx is thrown and React Query treats it as an error.
 */

const ENGINE_URL = import.meta.env.VITE_SYNC_ENGINE_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }

  /** A missing entity is a normal answer in support work, not a broken screen. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The Access session expired (401), or the engine does not allow this account (403). */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export type QueryParams = Record<string, string | number | string[] | null | undefined>;

function buildUrl(path: string, params?: QueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item));
    else if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const query = search.toString();
  return `${ENGINE_URL}/internal/v1${path}${query ? `?${query}` : ""}`;
}

export async function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
  // Accept is the only header, so the request stays simple: no preflight, which Access
  // would refuse for lacking the cookie.
  const response = await fetch(buildUrl(path, params), {
    headers: { Accept: "application/json" },
    credentials: "include",
    redirect: "manual",
  });

  // An expired Access session answers with a redirect to the login page, which a
  // cross-origin fetch cannot follow.
  if (response.type === "opaqueredirect") {
    renewSession();
    throw new ApiError(401, "The Cloudflare Access session has expired.");
  }
  if (!response.ok) {
    throw new ApiError(response.status, await readDetail(response));
  }
  return (await response.json()) as T;
}

const RENEWED_AT_KEY = "access-session-renewed-at";
const RENEW_INTERVAL_MS = 60_000;

/**
 * Both hosts share one Access session, so a top-level reload of the console renews the
 * engine's cookie too, and Access returns to the same URL. At most once a minute: if the
 * reload did not help, the error stays on screen instead of a reload loop.
 */
function renewSession(): void {
  try {
    const last = Number(sessionStorage.getItem(RENEWED_AT_KEY) ?? 0);
    if (Date.now() - last < RENEW_INTERVAL_MS) return;
    sessionStorage.setItem(RENEWED_AT_KEY, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
    return JSON.stringify(body);
  } catch {
    return `${response.status} ${response.statusText}`;
  }
}
